<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { geoMercator, geoPath } from 'd3-geo';
import { lighten } from 'color2k';
import {
  ZONE_MAP_LEGEND_SWATCHES,
  buildPaintedZonesFeatureCollection,
  isMappableFranceMapZone,
  frenchPostalToDepartmentCode,
  getMonacoMapPaint,
  MONACO_CENTER_LNG_LAT,
  MONACO_AREA_CODE,
} from './franceZoneMapUtils';

import departmentsData from './data/france-departments.json';

const props = defineProps({
  zones: {
    type: Array,
    required: true,
  },
});

const { t } = useI18n();

/** GeoJSON embarqué via Vite (évite fetch /public qui échoue selon l’origine). */
const SVG_W = 800;
const SVG_H = 600;

const departmentsCollection = ref(departmentsData);
const loadError = ref(false);
const loading = ref(false);
const hoveredCode = ref(null);
const tooltipPos = ref({ x: 0, y: 0 });

const hasAssignablePostal = computed(() =>
  props.zones.some(z => {
    if (!isMappableFranceMapZone(z)) return false;
    return (z.postal_codes || []).some(pc => frenchPostalToDepartmentCode(pc));
  })
);

const legendItems = computed(() =>
  props.zones
    .map((zone, zIndex) => ({
      id: zone.id,
      name: zone.name,
      swatchClass:
        ZONE_MAP_LEGEND_SWATCHES[zIndex % ZONE_MAP_LEGEND_SWATCHES.length],
      show: isMappableFranceMapZone(zone),
    }))
    .filter(x => x.show)
);

const paintedCollection = computed(() => {
  if (!departmentsCollection.value) return null;
  return buildPaintedZonesFeatureCollection(
    departmentsCollection.value,
    props.zones
  );
});

const MONACO_DOT_R = 5;

/** Départements en paths ; Monaco en cercle (évite le bug de path rectangulaire sur le polygone MC). */
const mapLayers = computed(() => {
  if (!paintedCollection.value) {
    return { displayPaths: [], monacoDot: null };
  }
  const fc = paintedCollection.value;
  const projection = geoMercator();
  const pathGen = geoPath(projection);
  projection.fitSize([SVG_W, SVG_H], fc);

  const baseRows = fc.features.map(f => {
    const p = f.properties;
    return {
      key: p.code,
      d: pathGen(f),
      mapFill: p.mapFill,
      mapFillOpacity: p.mapFillOpacity,
      mapStroke: p.mapStroke,
      nom: p.nom,
      zoneLabel: p.zoneLabel,
      code: p.code,
    };
  });

  const h = hoveredCode.value;
  const displayPaths = baseRows.map(row => {
    const isHover = h === row.code;
    let fill = row.mapFill;
    let fillOpacity = row.mapFillOpacity;
    if (isHover) {
      if (row.zoneLabel) {
        fill = lighten(row.mapFill, 0.06);
        fillOpacity = Math.min(1, row.mapFillOpacity + 0.1);
      } else {
        fill = '#e2e8f0';
        fillOpacity = 1;
      }
    }
    return { ...row, paintFill: fill, paintFillOpacity: fillOpacity };
  });

  const monacoPaint = getMonacoMapPaint(props.zones);
  const [cx, cy] = projection(MONACO_CENTER_LNG_LAT);
  let mFill = monacoPaint.mapFill;
  let mOpacity = monacoPaint.mapFillOpacity;
  if (h === MONACO_AREA_CODE) {
    if (monacoPaint.zoneLabel) {
      mFill = lighten(monacoPaint.mapFill, 0.06);
      mOpacity = Math.min(1, monacoPaint.mapFillOpacity + 0.1);
    } else {
      mFill = '#e2e8f0';
      mOpacity = 1;
    }
  }

  return {
    displayPaths,
    monacoDot: {
      cx,
      cy,
      r: MONACO_DOT_R,
      paintFill: mFill,
      paintFillOpacity: mOpacity,
      stroke: monacoPaint.mapStroke,
    },
  };
});

const hoveredRow = computed(() => {
  if (!hoveredCode.value) return null;
  if (hoveredCode.value === MONACO_AREA_CODE) {
    const paint = getMonacoMapPaint(props.zones);
    return {
      nom: 'Monaco',
      zoneLabel: paint.zoneLabel,
      code: MONACO_AREA_CODE,
    };
  }
  return (
    mapLayers.value.displayPaths.find(p => p.code === hoveredCode.value) || null
  );
});

const mapReady = computed(
  () => !loading.value && !loadError.value && departmentsCollection.value
);

function onPathEnter(code, event) {
  hoveredCode.value = code;
  tooltipPos.value = { x: event.clientX + 12, y: event.clientY + 12 };
}

function onSvgMouseMove(event) {
  if (hoveredCode.value) {
    tooltipPos.value = { x: event.clientX + 12, y: event.clientY + 12 };
  }
}

function onPathLeave() {
  hoveredCode.value = null;
}
</script>

<template>
  <div
    class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-sm"
  >
    <div class="border-b border-n-weak px-4 py-3">
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('TAXIS.ZONES.MAP_TITLE') }}
      </p>
      <p class="mt-0.5 text-xs text-n-slate-11">
        {{ t('TAXIS.ZONES.MAP_SUBTITLE') }}
      </p>
    </div>

    <div
      class="relative h-[min(360px,50vh)] w-full min-h-[260px] bg-n-solid-1"
      @mousemove="onSvgMouseMove"
    >
      <div
        v-if="loadError"
        class="absolute inset-0 z-10 flex items-center justify-center bg-n-alpha-1 px-4 text-center text-sm text-n-slate-11"
      >
        {{ t('TAXIS.ZONES.MAP_ERROR') }}
      </div>

      <div
        v-if="loading && !loadError"
        class="absolute inset-0 z-10 flex items-center justify-center bg-n-alpha-1"
      >
        <div class="flex gap-1.5">
          <div class="size-2 rounded-full bg-n-brand/40 animate-pulse" />
          <div
            class="size-2 rounded-full bg-n-brand/40 animate-pulse [animation-delay:200ms]"
          />
          <div
            class="size-2 rounded-full bg-n-brand/40 animate-pulse [animation-delay:400ms]"
          />
        </div>
      </div>

      <svg
        v-if="mapReady"
        class="rsm-svg h-full w-full touch-none [&_*]:outline-none"
        viewBox="0 0 800 600"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        :aria-label="t('TAXIS.ZONES.MAP_TITLE')"
      >
        <g class="text-n-slate-12">
          <path
            v-for="row in mapLayers.displayPaths"
            :key="row.key"
            :d="row.d"
            :fill="row.paintFill"
            :fill-opacity="row.paintFillOpacity"
            :stroke="row.mapStroke"
            stroke-width="0.5"
            vector-effect="non-scaling-stroke"
            class="cursor-pointer transition-[fill,fill-opacity] duration-150"
            @mouseenter="onPathEnter(row.code, $event)"
            @mouseleave="onPathLeave"
          />
          <circle
            v-if="mapLayers.monacoDot"
            :cx="mapLayers.monacoDot.cx"
            :cy="mapLayers.monacoDot.cy"
            :r="mapLayers.monacoDot.r"
            :fill="mapLayers.monacoDot.paintFill"
            :fill-opacity="mapLayers.monacoDot.paintFillOpacity"
            :stroke="mapLayers.monacoDot.stroke"
            stroke-width="0.5"
            vector-effect="non-scaling-stroke"
            class="cursor-pointer transition-[fill,fill-opacity] duration-150"
            @mouseenter="onPathEnter(MONACO_AREA_CODE, $event)"
            @mouseleave="onPathLeave"
          />
        </g>
      </svg>

      <div
        v-if="hoveredRow"
        class="pointer-events-none fixed z-50 max-w-xs rounded-md border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-left text-xs shadow-lg dark:bg-n-alpha-2"
        :style="{
          left: `${tooltipPos.x}px`,
          top: `${tooltipPos.y}px`,
        }"
      >
        <p class="font-medium text-n-slate-12">
          {{ hoveredRow.nom }}
        </p>
        <p v-if="hoveredRow.zoneLabel" class="mt-0.5 text-n-slate-11">
          {{
            t('TAXIS.ZONES.MAP_TOOLTIP_ZONE', {
              name: hoveredRow.zoneLabel,
            })
          }}
        </p>
        <p v-else class="mt-0.5 text-n-slate-10">
          {{ t('TAXIS.ZONES.MAP_TOOLTIP_NO_ASSIGNMENT') }}
        </p>
      </div>

      <div
        v-if="mapReady && legendItems.length && !hasAssignablePostal"
        class="pointer-events-none absolute bottom-3 left-3 right-3 rounded-lg bg-n-solid-1/90 px-3 py-2 text-center text-xs text-n-slate-11 shadow-sm backdrop-blur-sm dark:bg-n-alpha-2/95"
      >
        {{ t('TAXIS.ZONES.MAP_NO_FR_POSTAL') }}
      </div>
    </div>

    <div
      v-if="legendItems.length"
      class="flex flex-wrap gap-x-4 gap-y-2 border-t border-n-weak px-4 py-3"
    >
      <div
        v-for="item in legendItems"
        :key="item.id"
        class="flex items-center gap-2 text-xs text-n-slate-12"
      >
        <span
          class="size-3 shrink-0 rounded-sm ring-1 ring-n-weak"
          :class="item.swatchClass"
        />
        <span class="max-w-[14rem] truncate" :title="item.name">{{
          item.name
        }}</span>
      </div>
    </div>
  </div>
</template>
